import type { TagProps } from '@ads/components-react';
import dayjs from 'dayjs';

import type { ECourseModality } from '@/shared/api/generated';

/** Status (cor) da `Tag` do ADS por modalidade — uma cor distinta para cada. */
export type ModalityTagStatus = NonNullable<TagProps['status']>;

/** Placeholder exibido quando uma data está ausente/ inválida. */
export const EMPTY_DATE_PLACEHOLDER = '—';

/**
 * Formata uma data (ISO date-time) para `DD/MM/AAAA`.
 *
 * Usa apenas a parte de data (`YYYY-MM-DD`) para preservar o dia do calendário,
 * evitando o deslocamento de fuso que ocorreria ao converter o horário UTC para
 * o fuso local (ex.: `2025-06-30T00:00:00Z` viraria `29/06` em UTC-3).
 *
 * @param value - Data em string (date-time) vinda da API, ou nula.
 * @returns Data formatada, ou `—` quando ausente/ inválida.
 */
export function formatModuleDate(value: string | null | undefined): string {
  if (!value) return EMPTY_DATE_PLACEHOLDER;
  const parsed = dayjs(value.slice(0, 10));
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : EMPTY_DATE_PLACEHOLDER;
}

/**
 * Mapeia cada valor de `ECourseModality` para a chave i18n do seu rótulo
 * (reaproveita as opções já usadas no formulário — namespace `classModuleManagement`).
 */
export const MODALITY_LABEL_KEY: Record<ECourseModality, string> = {
  1: 'general.modalityOptions.distance',
  2: 'general.modalityOptions.presential',
  3: 'general.modalityOptions.semiClassroom',
  4: 'general.modalityOptions.onDemand',
};

/** Cor da `Tag` por modalidade — cada modalidade recebe um status distinto. */
export const MODALITY_TAG_STATUS: Record<ECourseModality, ModalityTagStatus> = {
  1: 'highlight', // A distância
  2: 'positive', // Presencial
  3: 'helper', // Semipresencial
  4: 'negative', // Sob demanda
};
